import { useUIStore } from '@/core/stores/ui.store';
import type { NoteSummary } from '../types';

export interface NoteDateGroup {
  /** Stable key for the group — 'today' | 'last30Days' | a year like '2025'. */
  key: string;
  /** i18n key (relative to the `layout` namespace) for 'today'/'last30Days'. Absent for year groups — the year itself is the label. */
  labelKey?: string;
  notes: NoteSummary[];
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Calendar date (YYYY-MM-DD) in a given IANA timezone — sidesteps DST/offset math entirely. */
function calendarDateStr(date: Date, timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/** Whole calendar days between two YYYY-MM-DD strings (positive when `laterStr` is after `earlierStr`). */
function daysBetween(laterStr: string, earlierStr: string): number {
  const later = new Date(`${laterStr}T00:00:00Z`).getTime();
  const earlier = new Date(`${earlierStr}T00:00:00Z`).getTime();
  return Math.round((later - earlier) / DAY_MS);
}

/**
 * Buckets notes by `updatedAt` into Hoy / Últimos 30 días / per-year groups,
 * newest group first — mirrors the Figma sidebar grouping.
 *
 * @remarks
 * Buckets by calendar date in the user's configured timezone (`useUIStore`),
 * same pattern as `shared/utils/dateUtils.ts`'s `isTodayInTz` — comparing raw
 * `Date.getFullYear()`/`getDate()` uses the JS runtime's system timezone, which
 * can silently disagree with the user's configured one and misfile a note into
 * the wrong year right around a timezone's midnight boundary.
 *
 * @param now - injectable for tests; defaults to the current time.
 */
export function groupNotesByDate(notes: NoteSummary[], now: Date = new Date()): NoteDateGroup[] {
  const { timezone } = useUIStore.getState();
  const todayStr = calendarDateStr(now, timezone);

  const today: NoteSummary[] = [];
  const last30Days: NoteSummary[] = [];
  const byYear = new Map<number, NoteSummary[]>();

  for (const note of notes) {
    const noteDateStr = calendarDateStr(new Date(note.updatedAt), timezone);
    const daysAgo = daysBetween(todayStr, noteDateStr);

    if (daysAgo <= 0) {
      today.push(note);
    } else if (daysAgo <= 30) {
      last30Days.push(note);
    } else {
      const year = Number(noteDateStr.slice(0, 4));
      const yearNotes = byYear.get(year) ?? [];
      yearNotes.push(note);
      byYear.set(year, yearNotes);
    }
  }

  const groups: NoteDateGroup[] = [];
  if (today.length)
    groups.push({ key: 'today', labelKey: 'sidebar.notes.groups.today', notes: today });
  if (last30Days.length) {
    groups.push({
      key: 'last30Days',
      labelKey: 'sidebar.notes.groups.last30Days',
      notes: last30Days,
    });
  }

  [...byYear.keys()]
    .sort((a, b) => b - a)
    .forEach((year) => {
      groups.push({ key: String(year), notes: byYear.get(year) as NoteSummary[] });
    });

  return groups;
}
